import styled from '@emotion/styled';
import { Tooltip } from '@innovationdepartment/proxima-ui';

const DemographicOverviewSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px 40px;
  margin-left: 60px;
  margin-right: 60px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8e8e8;

  h2 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 1.8em;
    font-weight: 700;
  }

  .subtitle {
    margin: 0 0 30px 0;
    color: #777;
    font-size: 1em;
    line-height: 1.4;
  }

  .demographics-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 40px;
    width: 100%;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
      gap: 30px;
    }
  }
`;

const DemographicCard = styled.div`
  width: 100%;

  h3 {
    margin: 0 0 20px 0;
    color: #555;
    font-size: 1.1em;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .demographic-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .demographic-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    font-size: 0.9em;
  }

  .demographic-label {
    color: #333;
    font-weight: 500;
  }

  .demographic-percentage {
    color: #666;
    font-weight: 600;
    margin-left: auto;
  }

  .bar-container {
    background-color: #f1f5f9;
    border-radius: 4px;
    height: 8px;
    width: 100%;
    overflow: hidden;
    margin: 8px 0;
  }

  .bar {
    background-color: #7c3aed;
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`;

interface DemographicOverviewProps {
  overallPopulation:
    | {
        age_distribution?: Record<string, number>;
        gender_distribution?: Record<string, number>;
        residence_type_breakdown?: Record<string, number>;
      }
    | null
    | undefined;
}

const DemographicOverview = ({ overallPopulation }: DemographicOverviewProps) => {
  if (!overallPopulation) return null;

  const ageDistribution = overallPopulation.age_distribution || {};
  const genderDistribution = overallPopulation.gender_distribution || {};
  const residenceTypeBreakdown = overallPopulation.residence_type_breakdown || {};

  return (
    <DemographicOverviewSection>
      <h2>Demographic Overview</h2>
      <p className="subtitle">A snapshot of your audience&apos;s core demographic traits.</p>

      <div className="demographics-grid">
        {/* Age Distribution */}
        <DemographicCard>
          <h3>Age Distribution</h3>
          <ul className="demographic-list">
            {['18-24', '25-34', '35-44', '45-54', '55-64', '65+'].map(age => {
              const percentage = ageDistribution[age] || 0;
              return (
                <li key={age} className="demographic-item">
                  <div style={{ width: '100%' }}>
                    <div className="demographic-label">{age}</div>
                    <div className="bar-container">
                      <div className="bar" style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                  <div className="demographic-percentage">{percentage.toFixed(1)}%</div>
                </li>
              );
            })}
          </ul>
        </DemographicCard>

        {/* Geography Breakdown */}
        <DemographicCard>
          <h3>
            Geography Breakdown
            <Tooltip
              title="Breakdown of residence types across your customer base"
              placement="top"
              arrow
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: '#e2e8f0',
                  color: '#64748b',
                  fontSize: '12px',
                  cursor: 'help'
                }}
              >
                i
              </span>
            </Tooltip>
          </h3>
          <ul className="demographic-list">
            {[
              { key: 'URBAN', label: 'Urban' },
              { key: 'METRO_SPRAWL', label: 'Metro Sprawl' },
              { key: 'SUBURBAN', label: 'Suburban' },
              { key: 'RURAL', label: 'Rural' }
            ].map(({ key, label }) => {
              const percentage = residenceTypeBreakdown[key] || 0;
              return (
                <li key={key} className="demographic-item">
                  <div style={{ width: '100%' }}>
                    <div className="demographic-label">{label}</div>
                    <div className="bar-container">
                      <div className="bar" style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                  <div className="demographic-percentage">{percentage.toFixed(1)}%</div>
                </li>
              );
            })}
          </ul>
        </DemographicCard>

        {/* Gender Distribution */}
        <DemographicCard>
          <h3>Gender Distribution</h3>
          <ul className="demographic-list">
            {['Female', 'Male', 'Unknown'].map(gender => {
              const percentage = genderDistribution[gender] || 0;
              return (
                <li key={gender} className="demographic-item">
                  <div style={{ width: '100%' }}>
                    <div className="demographic-label">{gender}</div>
                    <div className="bar-container">
                      <div className="bar" style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                  <div className="demographic-percentage">{percentage.toFixed(1)}%</div>
                </li>
              );
            })}
          </ul>
        </DemographicCard>
      </div>
    </DemographicOverviewSection>
  );
};

export default DemographicOverview;
